import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Chip, Grid } from '@mui/material';
import { ComboSelect, TextboxInput, Typography } from '../../components/atoms';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoErrorIcon } from '../../assets/icons/info-error.svg';
import { useAppSelector } from '../../common/hooks';
// import Percent from '@mui/icons-material/Percent';
import dayjs from 'dayjs';
import { formatCurrency, getDurationColor, isMonthYearDatesEnabled, removeNonNumeric } from '../../utils';
import { addAgeToDate } from '../../common/helper';
import { DEFAULT_PLANNING_AGE, DEFAULT_RETIREMENT_AGE, DEFAULT_CURRENCY_SYMBOL } from '../../common/constants';
import { RadioWithOptionalDatePicker } from '../../components/molecules';
import { InfoMessage } from '../../themes/styles/common';

const ErrorSpan = styled('span')(({ theme }) => ({
    fontSize: '13px',
    fontStyle: 'italic',
    fontWeight: 400,
    lineHeight: '12px',
    color: theme.palette.berry[400],
    margin: 0
}));

const GridContainer = styled(Grid)(() => ({
    display: 'flex',
    flexDirection: 'column',
    '.MuiFormControl-root': {
        width: '100%'
    },
    gap: '0.5rem'
}));

const StyledForm = styled('form')(() => ({}));

export const AddIncomeSourcesForm = ({
    onSubmit,
    handleSubmit,
    setValue,
    control,
    errors,
    sourceOwnerOptions,
    keyId,
    getValues,
    trigger,
    clearErrors,
    beginningYearKey,
    endYearKey,
    onBeginningYearKeyChange,
    onEndYearKeyChange,
    needPrepopulation,
    needPrepopulationOnEdit,
    setNeedPrepopulation,
    setInfoMessage
}: any) => {
    const theme = useTheme();
    const sourceTypeDropdownValues = useAppSelector((state) => state.global.orgInfo?.incomeSourceType);
    const { t, i18n } = useTranslation();
    const [onEditPrepopulation, setOnEditPrepopulation] = useState(needPrepopulationOnEdit);
    const currentSourceTypeDropdownValues =
        sourceTypeDropdownValues[i18n.language] || sourceTypeDropdownValues['en'] || [];
    const endYearValue = useWatch({ control, name: 'endYear' });
    const beginningYearValue = useWatch({ control, name: 'beginningYear' });
    const {
        globalConfig: { currencySymbol }
    } = useAppSelector((state) => state.global);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const frequency = useWatch({ control, name: 'frequency' });

    const sourceOwnerValue = useWatch({ control, name: 'sourceOwner' });
    const sourceTypeValue = useWatch({ control, name: 'sourceType' });

    const userEditedSourceName = useRef(false);

    useEffect(() => {
        if (!sourceOwnerValue || !sourceTypeValue) return;
        if (!needPrepopulation || !onEditPrepopulation) return;
        let ownerName = '';
        if (sourceOwnerValue === 'primary') {
            ownerName = adviceClient?.clientFirstName || '';
        } else if (sourceOwnerValue === 'joint') {
            ownerName = t('TEXT_JOINT');
        } else {
            ownerName =
                adviceClient?.relationship?.find((r) => r.id === sourceOwnerValue)?.firstName ||
                adviceClient?.relationship?.[0]?.firstName ||
                '';
        }
        const currentSourceType =
            currentSourceTypeDropdownValues.find((item: Record<string, string>) => item.value === sourceTypeValue)
                ?.label || sourceTypeValue;
        const autoName =
            sourceOwnerValue === 'joint' ? `${ownerName} ${currentSourceType}` : `${ownerName}'s ${currentSourceType}`;

        setValue('sourceName', autoName, { shouldValidate: true, shouldDirty: true });
        clearErrors?.('sourceName');
        trigger?.('sourceName');
    }, [sourceOwnerValue, sourceTypeValue, adviceClient, setValue, clearErrors, trigger]);

    const handleAmountChange = (value: string) => {
        const sanitizedValue = removeNonNumeric(value || '');

        if (!sanitizedValue) {
            setValue('amount', '', {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
            });
            return;
        }

        const numericValue = Number(sanitizedValue);
        setValue('amount', isNaN(numericValue) ? '' : numericValue, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
        });
    };
    // eslint-disable-next-line no-unused-vars
    const handleDateChange = (value: dayjs.Dayjs, onChange: (event: dayjs.Dayjs) => void, fieldName: string) => {
        if (frequency === 'Annually') {
            onChange(value?.startOf('year'));
        } else {
            onChange(value?.startOf('month'));
        }

        // Trigger validation on endYear if beginningYear changed
        if (fieldName === 'beginningYear') {
            const endYear = getValues('endYear');
            if (endYear) {
                trigger('endYear'); // Only trigger validation if endYear has been selected
            }
        }
    };
    const isSingleClientOnly = !adviceClient?.relationship || adviceClient?.relationship.length === 0;

    const getIncomeSameDatesInfoMessage = () => {
        if (!frequency || !beginningYearValue || !endYearValue) {
            return null;
        }

        const startDate = dayjs(beginningYearValue, 'DD-MM-YYYY');
        const endDate = dayjs(endYearValue, 'DD-MM-YYYY');

        if (!startDate.isValid() || !endDate.isValid()) {
            return null;
        }
        if (isMonthYearDatesEnabled()) {
            if (startDate.year() === endDate.year() && startDate.month() === endDate.month()) {
                return t('INCOME_DATES_SAME_MONTH_ERROR');
            }
        } else if (frequency === 'Annually') {
            if (startDate.year() === endDate.year()) {
                return t('INCOME_DATES_SAME_YEAR_ERROR');
            }
        } else if (startDate.year() === endDate.year() && startDate.month() === endDate.month()) {
            return t('INCOME_DATES_SAME_MONTH_ERROR');
        }

        return null;
    };

    const infoMessage = useMemo(() => {
        const msg = getIncomeSameDatesInfoMessage();
        if (setInfoMessage) {
            setInfoMessage(msg);
        }
        return msg;
    }, [frequency, beginningYearValue, endYearValue, t]);

    return (
        <StyledForm
            onSubmit={handleSubmit(onSubmit)}
            id="add-income-sources-form"
            data-testid="add-income-sources-form"
            key={keyId}
        >
            <Grid container gap={2} fontWeight="500">
                <>
                    <GridContainer item xs={12} key="sourceOwnerContainer">
                        {isSingleClientOnly ? (
                            <>
                                <Typography id="source-owner-label" variant="labels.large">{`${t(
                                    'INCOME_SOURCE_OWNER'
                                )} *`}</Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 600, color: theme.palette.semantic.text.default }}
                                >
                                    {`${adviceClient?.clientFirstName ?? ''} ${
                                        adviceClient?.clientLastName ?? ''
                                    }`.trim() || t('TEXT_CLIENT')}
                                </Typography>
                            </>
                        ) : (
                            <Controller
                                name="sourceOwner"
                                control={control}
                                rules={{
                                    required: { value: true, message: t('SOURCE_OWNER_IS_REQUIRED') }
                                }}
                                render={({ field: { onChange, value, name, onBlur } }) => (
                                    <ComboSelect
                                        onSelectChange={(val) => {
                                            setNeedPrepopulation && setNeedPrepopulation(true);
                                            setOnEditPrepopulation(true);
                                            onChange(val);
                                        }}
                                        onBlur={onBlur}
                                        selectedValue={value}
                                        name={`add-income-sources-${name}`}
                                        label={`${t('INCOME_SOURCE_OWNER')} *`}
                                        placeholder={t('TEXT_SELECT')}
                                        options={sourceOwnerOptions}
                                        error={!!errors.sourceOwner}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    border: `1px solid ${theme.palette.semantic.bd.default}`,
                                                    borderRadius: '8px',
                                                    '& .MuiMenuItem-root': {
                                                        color: theme.palette.semantic.text.default
                                                    }
                                                }
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected)
                                                return (
                                                    <em
                                                        style={{
                                                            color: theme.palette.semantic.int.link,
                                                            fontStyle: 'normal'
                                                        }}
                                                    >
                                                        {t('TEXT_SELECT')}
                                                    </em>
                                                );

                                            if (selected === 'primary') {
                                                return `${adviceClient?.clientFirstName ?? ''} ${
                                                    adviceClient?.clientLastName ?? ''
                                                }`.trim();
                                            }
                                            if (selected === 'joint') {
                                                return t('TEXT_JOINT');
                                            }
                                            const relationship = adviceClient?.relationship?.find(
                                                (r) => r.id === selected
                                            );
                                            if (relationship) {
                                                return `${relationship.firstName ?? ''} ${
                                                    relationship.lastName ?? ''
                                                }`.trim();
                                            }
                                            return selected;
                                        }}
                                    />
                                )}
                            />
                        )}
                        {errors.sourceOwner && <ErrorSpan role="alert">{errors.sourceOwner.message}</ErrorSpan>}
                    </GridContainer>

                    <GridContainer item xs={12} key="sourceTypeContainer">
                        <Controller
                            name="sourceType"
                            control={control}
                            rules={{
                                required: { value: true, message: t('REQUIRED_SOURCE_TYPE') }
                            }}
                            render={({ field: { onChange, value, name, onBlur } }) => (
                                <ComboSelect
                                    onSelectChange={(val) => {
                                        setNeedPrepopulation && setNeedPrepopulation(true);
                                        setOnEditPrepopulation(true);
                                        onChange(val);
                                    }}
                                    onBlur={onBlur}
                                    selectedValue={value}
                                    name={`add-income-sources-${name}`}
                                    label={`${t('SOURCE_TYPE')} *`}
                                    placeholder={t('TEXT_SELECT')}
                                    options={currentSourceTypeDropdownValues}
                                    error={!!errors.sourceType}
                                    listItemStyle={{
                                        padding: '0px 14px'
                                    }}
                                    renderMenuItem={(option) => (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                            {option.duration && (
                                                <Chip
                                                    label={t(option.duration.split(' ')[0].toUpperCase())}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getDurationColor(theme, option.duration),
                                                        fontWeight: 600,
                                                        minWidth: '100px',
                                                        borderRadius: '16px'
                                                    }}
                                                />
                                            )}
                                            <Box component="span">
                                                {option.label}
                                            </Box>
                                        </Box>
                                    )}
                                    renderValue={(selected) => {
                                        if (!selected)
                                            return (
                                                <em
                                                    style={{
                                                        color: theme.palette.semantic.int.link,
                                                        fontStyle: 'normal'
                                                    }}
                                                >
                                                    {t('TEXT_SELECT')}
                                                </em>
                                            );
                                        const selectedOption = currentSourceTypeDropdownValues.find(
                                            (option: { value: any }) => option.value === selected
                                        );
                                        if (selectedOption) {
                                            return (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        width: '100%'
                                                    }}
                                                >
                                                    {selectedOption.duration && (
                                                        <Chip
                                                            label={t(
                                                                selectedOption.duration.split(' ')[0].toUpperCase()
                                                            )}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: getDurationColor(
                                                                    theme,
                                                                    selectedOption.duration
                                                                ),
                                                                fontWeight: 600,
                                                                minWidth: '100px',
                                                                borderRadius: '16px'
                                                            }}
                                                        />
                                                    )}
                                                    <Box component="span" sx={{ fontWeight: 400 }}>
                                                        {selectedOption.label}
                                                    </Box>
                                                </Box>
                                            );
                                        }
                                    }}
                                />
                            )}
                        />
                        {errors.sourceType && <ErrorSpan role="alert">{errors.sourceType.message}</ErrorSpan>}
                    </GridContainer>
                    <GridContainer item xs={12} key="sourceNameContainer">
                        <Controller
                            name="sourceName"
                            control={control}
                            defaultValue=""
                            rules={{
                                required: { value: true, message: t('REQUIRED_SOURCE_NAME') },
                                pattern: {
                                    value: /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i,
                                    message: t('SOURCE_NAME_VALIDATION_MESSAGE')
                                },
                                maxLength: {
                                    value: 50,
                                    message: t('NAME_MAX_LENGTH_VALIDATION')
                                }
                            }}
                            render={({ field: { onChange, onBlur, value, ref } }) => (
                                <>
                                    <Typography id="source-name-input-label" variant="labels.large">{`${t(
                                        'SOURCE_NAME'
                                    )} *`}</Typography>
                                    <TextboxInput
                                        error={!!errors.sourceName}
                                        type="text"
                                        fullWidth
                                        onBlur={onBlur}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val.trim() === '') {
                                                userEditedSourceName.current = false;
                                            } else {
                                                userEditedSourceName.current = true;
                                            }
                                            onChange(val);
                                        }}
                                        value={value}
                                        inputRef={ref}
                                    />
                                </>
                            )}
                        />
                        {errors.sourceName && <ErrorSpan role="alert">{errors.sourceName.message}</ErrorSpan>}
                    </GridContainer>
                </>
                {infoMessage && (
                    <InfoMessage>
                        <InfoErrorIcon />
                        <span>{infoMessage}</span>
                    </InfoMessage>
                )}
                <Grid sx={{ display: 'flex', width: '100%' }} item gap={1}>
                    <GridContainer xs={6} item key="amountContainer">
                        <Controller
                            name="amount"
                            control={control}
                            rules={{
                                required: { value: true, message: t('TEXT_THIS_IS_REQUIRED_FIELD') },
                                validate: {
                                    weight: (value) => {
                                        if (value === '' || value === undefined || value === null) return true;
                                        return Number(value) <= 0 ? t('TEXT_AMOUNT_VALIDATION') : true;
                                    }
                                }
                            }}
                            defaultValue={''}
                            render={({ field: { onBlur, value, ref, name } }) => (
                                <>
                                    <Typography id="amount-input-label" variant="labels.large">{`${t(
                                        'AMOUNT'
                                    )} *`}</Typography>
                                    <TextboxInput
                                        onBlur={() => {
                                            onBlur();
                                        }}
                                        onChange={(e) => {
                                            handleAmountChange(e.target.value);
                                        }}
                                        value={
                                            value === undefined || value === null || value === ''
                                                ? ''
                                                : formatCurrency(+value, true)
                                        }
                                        name={`add-income-sources-${name}`}
                                        inputRef={ref}
                                        error={!!errors.amount}
                                        min={undefined}
                                        type="text"
                                        placeholder={currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                                    />
                                </>
                            )}
                        />
                        {errors.amount && <ErrorSpan role="alert">{errors.amount.message}</ErrorSpan>}
                    </GridContainer>
                    <GridContainer xs={6} sx={{ flexGrow: '1' }} item key="frequencyContainer">
                        <Controller
                            name="frequency"
                            control={control}
                            rules={{
                                required: { value: true, message: t('FREQUENCY_IS_REQUIRED') }
                            }}
                            defaultValue={undefined}
                            render={({ field: { onBlur, value, name } }) => (
                                <ComboSelect
                                    onSelectChange={async (val: string) => {
                                        setValue('frequency', val, { shouldValidate: true });
                                        if (endYearValue) {
                                            await trigger('endYear');
                                            await trigger('beginningYear');
                                        }
                                    }}
                                    onBlur={onBlur}
                                    selectedValue={value}
                                    name={`add-income-sources-${name}`}
                                    label={`${t('FREQUENCY')} *`}
                                    placeholder={t('TEXT_SELECT')}
                                    options={[
                                        { label: t('ANNUALLY'), value: 'Annually' },
                                        { label: t('MONTHLY'), value: 'Monthly' }
                                    ]}
                                    error={errors.frequency}
                                />
                            )}
                        />
                        {errors.frequency && <ErrorSpan role="alert">{errors.frequency.message}</ErrorSpan>}
                    </GridContainer>
                </Grid>
                <div className="go-horizontal-align-drawer">
                    <GridContainer item xs={12} key="beginningYearContainer">
                        <Controller
                            name="beginningYear"
                            control={control}
                            rules={{
                                required: true,
                                validate: (value) => {
                                    const date = dayjs(value, 'DD-MM-YYYY');
                                    if (!date.isValid()) return t('TEXT_VALID_DATE_ERROR');

                                    const now = dayjs();
                                    if (isMonthYearDatesEnabled()) {
                                        // All frequencies use MM/YYYY: allow the current month and future months.
                                        if (date.isBefore(now, 'month')) {
                                            return t('TEXT_VALID_DATE_ERROR');
                                        }
                                    } else if (getValues('frequency') === 'Annually') {
                                        if (date.year() <= now.year()) {
                                            return t('TEXT_VALID_DATE_ERROR');
                                        }
                                    } else if (date.isBefore(now, 'month')) {
                                        return t('TEXT_VALID_DATE_ERROR');
                                    }
                                    return true;
                                }
                            }}
                            render={({ field }) => (
                                <>
                                    <Typography
                                        id="beginning-year-input-label"
                                        variant="labels.large"
                                        sx={{ color: theme.palette.grey[300] }}
                                        label={`${t('STARTING_AT_LABEL')}*`}
                                    />
                                    <RadioWithOptionalDatePicker
                                        field={{
                                            ...field,
                                            onChange: (arg: string) => {
                                                field.onChange(arg);
                                                trigger('endYear');
                                            }
                                        }}
                                        selectedKey={beginningYearKey}
                                        onSelectedKeyChange={onBeginningYearKeyChange}
                                        hasError={!!errors.beginningYear && errors.beginningYear.type !== 'required'}
                                        enabled={!!getValues('frequency')}
                                        isMMYYYY={isMonthYearDatesEnabled()}
                                        isYYYY={!isMonthYearDatesEnabled() && getValues('frequency') === 'Annually'}
                                        options={[
                                            {
                                                label: (
                                                    <>
                                                        <Typography
                                                            variant="labels.largeDemibold"
                                                            label={t('CLIENT_RET_DATE_LABEL')}
                                                        />
                                                        <Typography
                                                            variant="labels.large"
                                                            label={`${addAgeToDate(
                                                                adviceClient?.clientDob,
                                                                adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE,
                                                                'MMMM YYYY'
                                                            )}, ${t('AGE_LABEL')} ${
                                                                adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE
                                                            }`}
                                                        />
                                                    </>
                                                ),
                                                value: addAgeToDate(
                                                    adviceClient?.clientDob,
                                                    adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE
                                                ),
                                                disabled: !getValues('frequency')
                                            },
                                            adviceClient?.relationship?.[0]?.age && {
                                                label: (
                                                    <>
                                                        <Typography
                                                            variant="labels.largeDemibold"
                                                            label={t('COCLIENT_RET_DATE_LABEL')}
                                                        />
                                                        <Typography
                                                            variant="labels.large"
                                                            label={`${addAgeToDate(
                                                                adviceClient?.relationship?.[0]?.age,
                                                                adviceClient?.relationship?.[0]?.retirementAge ||
                                                                    DEFAULT_RETIREMENT_AGE,
                                                                'MMMM YYYY'
                                                            )}, ${t('AGE_LABEL')} ${
                                                                adviceClient?.relationship?.[0]?.retirementAge ||
                                                                DEFAULT_RETIREMENT_AGE
                                                            }`}
                                                        />
                                                    </>
                                                ),
                                                value: addAgeToDate(
                                                    adviceClient?.relationship?.[0]?.age,
                                                    adviceClient?.relationship?.[0]?.retirementAge ||
                                                        DEFAULT_RETIREMENT_AGE
                                                ),
                                                disabled: !getValues('frequency')
                                            }
                                        ]}
                                    />
                                </>
                            )}
                        />
                        {errors.beginningYear && <ErrorSpan role="alert">{errors.beginningYear.message}</ErrorSpan>}
                    </GridContainer>
                    <GridContainer item xs={12} key="endYearContainer">
                        <Controller
                            name="endYear"
                            control={control}
                            rules={{
                                required: true,
                                validate: (value) => {
                                    const endDate = dayjs(value, 'DD-MM-YYYY');
                                    if (!endDate.isValid()) return t('TEXT_VALID_END_DATE_ERROR');

                                    const rawStartDate = getValues('beginningYear');
                                    const startDate = dayjs(rawStartDate, 'DD-MM-YYYY');
                                    if (!startDate.isValid()) return;
                                    if (isMonthYearDatesEnabled()) {
                                        // All frequencies use MM/YYYY: the end month must be after the start month.
                                        if (!endDate.isAfter(startDate, 'month')) {
                                            return t('TEXT_VALID_END_DATE_ERROR');
                                        }
                                    } else if (getValues('frequency') === 'Annually') {
                                        if (startDate.year() === endDate.year()) {
                                            return t('TEXT_START_END_YEAR_SHOULD_NOT_BE_SAME');
                                        }
                                        if (endDate.year() < startDate.year()) {
                                            return t('TEXT_VALID_END_DATE_ERROR');
                                        }
                                    } else if (!endDate.isAfter(startDate, 'month')) {
                                        return t('TEXT_VALID_END_DATE_ERROR');
                                    }
                                    return true;
                                }
                            }}
                            defaultValue={null}
                            render={({ field }) => (
                                <>
                                    <Typography
                                        id="end-year-input-label"
                                        variant="labels.large"
                                        sx={{ color: theme.palette.neutral[300] }}
                                        label={`${t('ENDING_AT_LABEL')}*`}
                                    />
                                    <RadioWithOptionalDatePicker
                                        field={{
                                            ...field,
                                            onChange: (arg: string) => {
                                                field.onChange(arg);
                                                trigger('beginningYear');
                                                field.onBlur();
                                            }
                                        }}
                                        selectedKey={endYearKey}
                                        onSelectedKeyChange={onEndYearKeyChange}
                                        hasError={!!errors.endYear && errors.endYear.type !== 'required'}
                                        enabled={!!getValues('frequency')}
                                        options={[
                                            {
                                                label: (
                                                    <>
                                                        <Typography
                                                            variant="labels.largeDemibold"
                                                            label={t('CLIENT_PLAN_DATE_LABEL')}
                                                        />
                                                        <Typography
                                                            variant="labels.large"
                                                            label={`${addAgeToDate(
                                                                adviceClient?.clientDob,
                                                                adviceClient?.planningAge || DEFAULT_PLANNING_AGE,
                                                                'MMMM YYYY'
                                                            )}, ${t('AGE_LABEL')} ${
                                                                adviceClient?.planningAge || DEFAULT_PLANNING_AGE
                                                            }`}
                                                        />
                                                    </>
                                                ),
                                                value: addAgeToDate(
                                                    adviceClient?.clientDob,
                                                    adviceClient?.planningAge || DEFAULT_PLANNING_AGE
                                                ),
                                                disabled: !getValues('frequency')
                                            },
                                            adviceClient?.relationship?.[0]?.age && {
                                                label: (
                                                    <>
                                                        <Typography
                                                            variant="labels.largeDemibold"
                                                            label={t('COCLIENT_PLAN_DATE_LABEL')}
                                                        />
                                                        <Typography
                                                            variant="labels.large"
                                                            label={`${addAgeToDate(
                                                                adviceClient?.relationship?.[0]?.age,
                                                                adviceClient?.relationship?.[0]?.planningAge ||
                                                                    DEFAULT_PLANNING_AGE,
                                                                'MMMM YYYY'
                                                            )}, ${t('AGE_LABEL')} ${
                                                                adviceClient?.relationship?.[0]?.planningAge ||
                                                                DEFAULT_PLANNING_AGE
                                                            }`}
                                                        />
                                                    </>
                                                ),
                                                value: addAgeToDate(
                                                    adviceClient?.relationship?.[0]?.age,
                                                    adviceClient?.relationship?.[0]?.planningAge ||
                                                        DEFAULT_PLANNING_AGE,
                                                    'DD-MM-YYYY'
                                                ),
                                                disabled: !getValues('frequency')
                                            }
                                        ]}
                                        isMMYYYY={isMonthYearDatesEnabled()}
                                        isYYYY={!isMonthYearDatesEnabled() && getValues('frequency') === 'Annually'}
                                    />
                                </>
                            )}
                        />
                        {errors.endYear && <ErrorSpan role="alert">{errors.endYear.message}</ErrorSpan>}
                    </GridContainer>
                </div>
                {/* Commenting as per AEX-1253 */}
                {/* <GridContainer item xs={12} key="escalateIncomeContainer">
                    <Controller
                        name="escalateIncome"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: t('ESCALATION_INCOME_REQUIRED')
                            },
                            max: {
                                value: 10,
                                message: t('TEXT_MAX_VALUE')
                            },
                            min: {
                                value: 0,
                                message: t('TEXT_MIN_VALUE')
                            }
                        }}
                        defaultValue={undefined}
                        render={({ field: { value, name, onBlur, onChange, ref } }) => (
                            <>
                                <InputLabel id="escalation-icome-input-label">{`${t(
                                    'ESCALATE_INCCOME'
                                )} *`}</InputLabel>
                                <TextboxInput
                                    onBlur={onBlur}
                                    onChange={(e) => onChange(parseInt(e.target.value))}
                                    value={value}
                                    name={`add-income-sources-${name}`}
                                    inputRef={ref}
                                    error={!!errors.escalateIncome}
                                    min={0}
                                    max={10}
                                    type="number"
                                    placeholder={t('ESCALATE_INCCOME')}
                                    icon={<Percent />}
                                    iconPlacement="end"
                                />
                            </>
                        )}
                    />
                    {errors.escalateIncome && <ErrorSpan role="alert">{errors.escalateIncome.message}</ErrorSpan>}
                </GridContainer> */}
            </Grid>
        </StyledForm>
    );
};
